import { formatPairingCode } from '@forgesworn/joystick-protocol';
/**
 * Pad home screen. Shown when the URL carries no pairing token — i.e.
 * the user opened the installed PWA from their home screen rather
 * than scanning a QR with the phone's camera app.
 *
 * Three pairing paths, listed by speed:
 *   1. Live camera scan (jsqr) — best when the PWA is installed and
 *      the user can see the game QR.
 *   2. File upload / take photo — fallback when camera access fails.
 *   3. Manual code entry — fallback when scanning isn't possible.
 *
 * On a successful match we extract the session id (from the scanned
 * URL or typed code) and reload at /?s=<id>, which the main.ts router
 * hands to startSession. Mirrors the mobile.pallasite.app controller
 * home page pattern — the model is well-tested for solo dev use.
 */

import jsQR from 'jsqr';
import { clearPendingSession, readPendingSession, renderInstallHint } from './install-first.js';
import { renderTournamentPanel } from './tournament.js';
import { renderProfileChip } from './account-ui.js';
import type { Account } from './identity.js';
import { parsePairingInput, pairingPath } from './pairing.js';

export function renderScanner(root: HTMLElement, account?: Account | null): void {
  root.replaceChildren();
  // Switch the root out of grid layout so the home view scrolls
  // naturally instead of fighting the controller-style grid template.
  root.classList.add('home-mode');
  root.style.backgroundColor = '';
  root.classList.remove('profile-mode');

  const home = document.createElement('div');
  home.className = 'home';

  // Force-refresh chip in the top-right of the scanner home.
  const refresh = document.createElement('button');
  refresh.type = 'button';
  refresh.className = 'home-refresh';
  refresh.title = 'Force refresh — clears the offline cache';
  refresh.setAttribute('aria-label', 'Force refresh');
  refresh.textContent = '↻ REFRESH';
  refresh.addEventListener('click', async () => {
    if (!confirm('Force refresh? Clears the offline cache and reloads.')) return;
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) await r.unregister();
      }
      if ('caches' in window) {
        const names = await caches.keys();
        for (const n of names) await caches.delete(n);
      }
    } catch { /* ignore */ }
    window.location.replace('/?_ts=' + Date.now());
  });
  home.appendChild(refresh);

  const header = document.createElement('div');
  header.className = 'home-header';
  header.innerHTML = `
    <h1>JOYSTICK PAD</h1>
    <p>Scan a game QR or type the session code to pair.</p>
  `;
  home.appendChild(header);
  const appChoice = document.createElement('a'); appChoice.href = '/get.html'; appChoice.textContent = 'Browser, iPhone, iPad or Android'; appChoice.className = 'controller-choice'; home.appendChild(appChoice);


  // Returning player: show their initials, tap the chip to change them.
  if (account) renderProfileChip(home, account);

  // Pending resume — if the user scanned a game QR before installing
  // the PWA, the install-first gate stashed the session id. Offer a
  // one-tap resume so they don't have to scan again.
  const pending = readPendingSession();
  if (pending) {
    const resume = document.createElement('div');
    resume.className = 'card card-resume';
    resume.innerHTML = `
      <div class="card-title">RESUME PAIRING</div>
      <div class="resume-sub">Session <code>${escapeHtml(formatPairingCode(pending.sessionId))}</code></div>
    `;
    const resumeBtn = document.createElement('button');
    resumeBtn.className = 'btn primary';
    resumeBtn.textContent = 'PAIR NOW';
    resume.appendChild(resumeBtn);
    const skipBtn = document.createElement('button');
    skipBtn.className = 'btn ghost';
    skipBtn.textContent = 'SKIP — SCAN A NEW QR';
    resume.appendChild(skipBtn);
    resumeBtn.addEventListener('click', () => {
      clearPendingSession();
      window.location.assign(pairingPath(pending));
    });
    skipBtn.addEventListener('click', () => {
      clearPendingSession();
      resume.remove();
    });
    home.appendChild(resume);
  }

  // ── Scan card ──────────────────────────────────────────────────────
  const scanCard = document.createElement('div');
  scanCard.className = 'card card-scan';
  const scanLabel = document.createElement('div');
  scanLabel.className = 'card-title';
  scanLabel.textContent = 'SCAN QR';
  scanCard.appendChild(scanLabel);

  const scanRow = document.createElement('div');
  scanRow.className = 'row';
  const scanBtn = document.createElement('button');
  scanBtn.className = 'btn primary';
  scanBtn.textContent = '📷  CAMERA';
  const fileBtn = document.createElement('button');
  fileBtn.className = 'btn ghost';
  fileBtn.textContent = '🖼  PHOTO';
  scanRow.appendChild(scanBtn);
  scanRow.appendChild(fileBtn);
  scanCard.appendChild(scanRow);

  // File input is hidden — only triggered by clicking PHOTO.
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.setAttribute('capture', 'environment');
  fileInput.style.display = 'none';
  scanCard.appendChild(fileInput);

  // The live-camera host. Mounted only while a camera stream is active.
  const scanHost = document.createElement('div');
  scanHost.className = 'scan-host';
  scanHost.style.display = 'none';
  const video = document.createElement('video');
  video.playsInline = true;
  video.muted = true;
  const scanStatus = document.createElement('p');
  scanStatus.className = 'scan-status';
  scanStatus.textContent = 'Point at the QR on the game screen.';
  // Zoom controls -- shown only when the active camera track exposes a
  // zoom capability (iOS Safari 14.3+, modern Android). Phones used
  // from across the room need 2-4x to give jsQR enough modules to
  // lock onto.
  const zoomRow = document.createElement('div');
  zoomRow.className = 'scan-zoom-row';
  const zoomOut = document.createElement('button');
  zoomOut.className = 'scan-zoom-btn';
  zoomOut.type = 'button';
  zoomOut.textContent = '−';
  zoomOut.setAttribute('aria-label', 'zoom out');
  const zoomLabel = document.createElement('span');
  zoomLabel.className = 'scan-zoom-label';
  zoomLabel.textContent = '1.0x';
  const zoomIn = document.createElement('button');
  zoomIn.className = 'scan-zoom-btn';
  zoomIn.type = 'button';
  zoomIn.textContent = '+';
  zoomIn.setAttribute('aria-label', 'zoom in');
  zoomRow.appendChild(zoomOut);
  zoomRow.appendChild(zoomLabel);
  zoomRow.appendChild(zoomIn);
  zoomRow.style.display = 'none';
  const stopBtn = document.createElement('button');
  stopBtn.className = 'btn ghost';
  stopBtn.textContent = 'CANCEL';
  scanHost.appendChild(video);
  scanHost.appendChild(zoomRow);
  scanHost.appendChild(scanStatus);
  scanHost.appendChild(stopBtn);
  scanCard.appendChild(scanHost);

  home.appendChild(scanCard);

  // ── Manual code card ──────────────────────────────────────────────
  const codeCard = document.createElement('div');
  codeCard.className = 'card card-code';
  const codeLabel = document.createElement('div');
  codeLabel.className = 'card-title';
  codeLabel.textContent = 'OR ENTER CODE';
  codeCard.appendChild(codeLabel);
  const codeInput = document.createElement('input');
  codeInput.type = 'text';
  codeInput.setAttribute('inputmode', 'text');
  codeInput.setAttribute('autocapitalize', 'characters');
  codeInput.setAttribute('autocomplete', 'off');
  codeInput.setAttribute('spellcheck', 'false');
  codeInput.placeholder = 'ABC DEF';
  codeCard.appendChild(codeInput);
  const codeBtn = document.createElement('button');
  codeBtn.className = 'btn primary';
  codeBtn.textContent = 'PAIR';
  codeCard.appendChild(codeBtn);
  const codeHint = document.createElement('p');
  codeHint.className = 'card-hint';
  const HINT_DEFAULT = 'Enter the six characters under the game QR, for example ABC DEF. You can also paste a pairing link.';
  codeHint.textContent = HINT_DEFAULT;
  codeCard.appendChild(codeHint);
  home.appendChild(codeCard);
  // Optional install upgrade — dismissable, never blocks.
  renderInstallHint(home);

  renderTournamentPanel(home);
  root.appendChild(home);

  // ── State + handlers ──────────────────────────────────────────────
  let stream: MediaStream | null = null;
  let scanRaf: number | null = null;

  const setError = (msg: string | null, target = codeHint): void => {
    if (msg) {
      target.textContent = msg;
      target.classList.add('err');
    } else {
      target.textContent = HINT_DEFAULT;
      target.classList.remove('err');
    }
  };

  const handleResult = (raw: string): boolean => {
    const token = parsePairingInput(raw);
    if (!token) return false;
    stopScan();
    window.location.assign(pairingPath(token));
    return true;
  };

  const stopScan = (): void => {
    if (scanRaf !== null) { cancelAnimationFrame(scanRaf); scanRaf = null; }
    if (stream) { for (const t of stream.getTracks()) t.stop(); stream = null; }
    try { video.pause(); } catch { /* ignore */ }
    try { video.srcObject = null; } catch { /* ignore */ }
    scanHost.style.display = 'none';
    scanRow.style.display = 'flex';
    scanBtn.disabled = false;
    scanStatus.textContent = 'Point at the QR on the game screen.';
    scanStatus.classList.remove('err');
    zoomRow.style.display = 'none';
  };

  const startCamera = async (): Promise<void> => {
    scanBtn.disabled = true;
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      scanBtn.disabled = false;
      setError('This browser has no camera API — use PHOTO or type the code.');
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // Ask for 1080p where available. Phones used at sofa distance
        // benefit from the extra resolution -- jsQR has more module-
        // pixels to lock onto when the QR appears small in frame.
        video: {
          facingMode: { ideal: 'environment' },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
    } catch (err) {
      scanBtn.disabled = false;
      setError(err instanceof Error && err.name === 'NotAllowedError'
        ? 'Camera permission denied — use PHOTO or type the code.'
        : 'Could not open the camera — use PHOTO or type the code.');
      return;
    }
    scanRow.style.display = 'none';
    scanHost.style.display = 'flex';
    video.srcObject = stream;
    try { await video.play(); } catch { /* ignore */ }

    // ── Camera zoom ────────────────────────────────────────────────
    // iOS Safari 14.3+ and modern Android expose a `zoom` capability
    // on the active video track. When available, wire up the +/- row
    // and pinch-to-zoom so phones can scan QRs from across the room.
    type ZoomCap = { min: number; max: number; step?: number };
    type ZoomTrack = MediaStreamTrack & {
      getCapabilities?: () => { zoom?: ZoomCap };
      getSettings?: () => { zoom?: number };
    };
    const track = stream.getVideoTracks()[0] as ZoomTrack | undefined;
    let zoomCap: ZoomCap | null = null;
    let currentZoom = 1;
    if (track?.getCapabilities) {
      const caps = track.getCapabilities() as { zoom?: ZoomCap };
      const zCap = caps.zoom;
      if (zCap && Number.isFinite(zCap.min) && Number.isFinite(zCap.max) && zCap.max > zCap.min) {
        zoomCap = zCap;
        currentZoom = track.getSettings?.().zoom ?? zCap.min;
      }
    }
    const applyZoom = async (next: number): Promise<void> => {
      if (!zoomCap || !track) return;
      const clamped = Math.max(zoomCap.min, Math.min(zoomCap.max, next));
      currentZoom = clamped;
      try {
        await track.applyConstraints({
          advanced: [{ zoom: clamped }],
        } as unknown as MediaTrackConstraints);
        zoomLabel.textContent = `${clamped.toFixed(1)}x`;
      } catch { /* zoom rejected -- leave as-is */ }
    };
    if (zoomCap) {
      zoomRow.style.display = 'flex';
      // Default-step: pre-zoom to ~2x so a sofa-distance scan stands
      // a chance even before the user touches the controls.
      const initial = Math.min(zoomCap.max, Math.max(zoomCap.min, 2));
      void applyZoom(initial);
      const step = zoomCap.step && zoomCap.step > 0 ? zoomCap.step : Math.max(0.1, (zoomCap.max - zoomCap.min) / 16);
      zoomIn.addEventListener('click', () => { void applyZoom(currentZoom + step); });
      zoomOut.addEventListener('click', () => { void applyZoom(currentZoom - step); });
      // Pinch-to-zoom on the video element. Two-pointer gesture, scale
      // proportional to the ratio of finger distance vs. start.
      const activePointers = new Map<number, { x: number; y: number }>();
      let pinchStartDist = 0;
      let pinchStartZoom = 1;
      const dist = (): number => {
        const pts = [...activePointers.values()];
        if (pts.length < 2) return 0;
        const dx = pts[0]!.x - pts[1]!.x;
        const dy = pts[0]!.y - pts[1]!.y;
        return Math.hypot(dx, dy);
      };
      video.addEventListener('pointerdown', (e) => {
        activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (activePointers.size === 2) {
          pinchStartDist = dist();
          pinchStartZoom = currentZoom;
        }
      });
      video.addEventListener('pointermove', (e) => {
        if (!activePointers.has(e.pointerId)) return;
        activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (activePointers.size === 2 && pinchStartDist > 0) {
          const ratio = dist() / pinchStartDist;
          void applyZoom(pinchStartZoom * ratio);
        }
      });
      const dropPointer = (e: PointerEvent): void => { activePointers.delete(e.pointerId); };
      video.addEventListener('pointerup', dropPointer);
      video.addEventListener('pointercancel', dropPointer);
      video.addEventListener('pointerleave', dropPointer);
    }

    // Scan at higher resolution when available -- 1280 still runs at
    // 30fps on mid-range phones but gives jsQR ~4x the pixel-density
    // of the previous 640 budget, which is the difference between a
    // sofa-distance scan latching in 0.5s vs. timing out.
    const SCAN_TARGET_W = 1280;
    const canvas = document.createElement('canvas');
    const cctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!cctx) return;
    const scanStart = Date.now();
    let lastStatusTick = scanStart;
    const tick = (): void => {
      if (!stream) return;
      if (video.readyState >= 2 && video.videoWidth > 0) {
        const aspect = video.videoHeight / video.videoWidth;
        const w = Math.min(SCAN_TARGET_W, video.videoWidth);
        const h = Math.round(w * aspect);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        cctx.drawImage(video, 0, 0, w, h);
        try {
          const data = cctx.getImageData(0, 0, w, h);
          const found = jsQR(data.data, data.width, data.height, { inversionAttempts: 'attemptBoth' });
          if (found && handleResult(found.data)) return;
        } catch { /* keep scanning */ }
      }
      const now = Date.now();
      if (now - scanStart > 8_000 && now - lastStatusTick > 1_000) {
        scanStatus.textContent = 'Still scanning — tap CANCEL and type the code if it won\'t latch.';
        scanStatus.classList.add('err');
        lastStatusTick = now;
      }
      scanRaf = requestAnimationFrame(tick);
    };
    tick();
  };

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.src = url;
    try { await img.decode(); } catch {
      URL.revokeObjectURL(url);
      setError('Could not read that image.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const cctx = canvas.getContext('2d');
    if (!cctx) { URL.revokeObjectURL(url); return; }
    cctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    let data: ImageData;
    try { data = cctx.getImageData(0, 0, canvas.width, canvas.height); }
    catch { setError('Could not read image data.'); return; }
    const found = jsQR(data.data, data.width, data.height);
    if (!found || !handleResult(found.data)) {
      setError('No QR code found — try again or type the code.');
    }
  };

  scanBtn.addEventListener('click', () => { void startCamera(); });
  fileBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void handleFile(file);
    fileInput.value = '';
  });
  stopBtn.addEventListener('click', stopScan);

  const submitCode = (): void => {
    const token = parsePairingInput(codeInput.value);
    if (!token) {
      setError('Code must match the session shown under the game QR.');
      return;
    }
    codeInput.blur();
    stopScan();
    window.location.assign(pairingPath(token));
  };
  codeBtn.addEventListener('click', submitCode);
  codeInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submitCode(); }
  });
  codeInput.addEventListener('input', () => {
    if (!'joystick'.startsWith(codeInput.value.toLowerCase())) codeInput.value = formatPairingCode(codeInput.value);
    setError(null);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
